import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFilter } from '@spa/features/search_job/pc/contexts/FilterContext';
import {trackClickAllClear, trackClickJobFilterSearch} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useTrack} from "@spa/features/search_job/pc/contexts/TrackContext";

/**
 * Feature複数選択のフィルターコンポーネント
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {Array<{value: string|number, label: string}>} props.options
 * @param {string} props.categoryName
 * @param {string} props.featureParamKey
 * @param {string} [props.width="w330"]
 */
const MultiSelectFeatureFilter = ({
    label,
    options,
    categoryName,
    featureParamKey,
    width = "w330",
    translations,
}) => {
    const { handleFilterChange, getFilterValue } = useFilter();
    const [isOpen, setIsOpen] = useState(false);
    const [localSelectedValues, setLocalSelectedValues] = useState(null);

    const initialSelectedValuesRef = useRef(
        getFilterValue(featureParamKey, categoryName) || []
    );

    const currentValues = localSelectedValues !== null
        ? localSelectedValues
        : initialSelectedValuesRef.current;

    const displayIsActive = Boolean(initialSelectedValuesRef.current.length);
    const initialCount = initialSelectedValuesRef.current.length;

    const { channelName } = useTrack();

    useEffect(() => {
        if (isOpen) {
            setLocalSelectedValues(initialSelectedValuesRef.current);
        }
    }, [isOpen]);

    const handleOptionChange = useCallback((e, value) => {
        e.stopPropagation();
        setLocalSelectedValues(prevValues => {
            const currentVals = prevValues || initialSelectedValuesRef.current;
            return currentVals.includes(value)
                ? currentVals.filter(v => v !== value)
                : [...currentVals, value];
        });
    }, []);

    const handleClearAll = useCallback((e) => {
        trackClickAllClear(channelName);
        e.stopPropagation();
        setLocalSelectedValues([]);
    }, []);

    const handleApplyFilter = useCallback((e) => {
        trackClickJobFilterSearch(channelName);
        e.stopPropagation();
        handleFilterChange(
            featureParamKey,
            localSelectedValues?.length > 0 ? localSelectedValues : null,
            categoryName
        );
        setLocalSelectedValues(null);
        setIsOpen(false);
    }, [handleFilterChange, localSelectedValues, featureParamKey, categoryName]);

    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (e) => {
            if (e.target.closest('.p-filter_select_inner')) {
                return;
            }
            if (!e.target.closest(`.s-filter_btn_wrap[data-feature-key="${featureParamKey}"][data-category="${categoryName}"]`)) {
                setIsOpen(false);
                setLocalSelectedValues(null);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen, featureParamKey, categoryName]);

    const wrapperClassName = [
        "p-filter_btn_wrap",
        "p-filter_btn_wrap--select",
        "s-filter_btn_wrap",
        displayIsActive && "is-active",
        isOpen && "is-on"
    ].filter(Boolean).join(" ");

    const handleMainButtonClick = useCallback((e) => {
        e.stopPropagation();
        setIsOpen(prev => !prev);
    }, []);

    return (
        <li>
            <div
                className={wrapperClassName}
                data-feature-key={featureParamKey}
                data-category={categoryName}
            >
                <button
                    type="button"
                    className="p-filter_btn s-filter_btn"
                    aria-expanded={isOpen}
                    aria-label={translations.item_filter.replace(':item', label)}
                    onClick={handleMainButtonClick}
                >
                    {label}
                    {initialCount > 0 && (
                        <span className="p-filter_num">{initialCount}</span>
                    )}
                </button>
            </div>

            <div
                className={`p-filter_select_wrap s-filter_menu_box ${isOpen ? 'is-show' : ''}`}
                onClick={e => e.stopPropagation()}
            >
                <div
                    className={`p-filter_select_balloon p-filter_select_balloon--${width} s-filter_menu`}
                    tabIndex="0"
                    aria-label={translations.item_filter.replace(':item', label)}
                >
                    <div className="p-filter_select_inner u-pd20 s-filter_menu_inner">
                        <ul role="group">
                            {options.map((option) => (
                                <li key={option.value} className="c-checkbox_list">
                                    <label
                                        className="c-checkbox_label"
                                        aria-label={option.label}
                                    >
                                        <input
                                            type="checkbox"
                                            name={categoryName}
                                            value={option.value}
                                            checked={currentValues.includes(option.value.toString())}
                                            onChange={(e) => handleOptionChange(e, option.value.toString())}
                                            className="c-checkbox_default s-checkbox"
                                        />
                                        <span className="c-checkbox_btn" aria-hidden="true">
                                            {option.label}
                                        </span>
                                    </label>
                                </li>
                            ))}
                        </ul>

                        <div className="p-filter_footer s-filter_footer u-mt30">
                            <button
                                type="button"
                                className="p-filter_clear s-filter_clear"
                                onClick={handleClearAll}
                            >
                                {translations.all_clear}
                            </button>
                            <button
                                className="c-search_btn s-search_btn"
                                onClick={handleApplyFilter}
                            >
                                {translations.apply_filter}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    );
};

export default MultiSelectFeatureFilter;
